import {
  DETECTED_KIND_LABELS,
  DOCUMENT_LINK_LABELS,
  DOCUMENT_LINK_TYPES,
  type DocumentLinkType,
  EXTRACTION_DEPTH_HELP,
  EXTRACTION_DEPTH_LABELS,
  type ExtractionDepth,
  PIPELINE_STATUS_LABELS,
  SOURCE_CHANNEL_LABELS,
} from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, BookMarked, FileSearch, Lock, Trash2 } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { InCases } from '../components/cases/in-cases';
import { DataBlock, DataNumber, DataText } from '../components/data/data-text';
import { EntityLink } from '../components/entities/entity-link';
import { EraseSection } from '../components/erasure/erase-dialog';
import { Combobox } from '../components/guidance/combobox';
import { EmptyState } from '../components/guidance/empty-state';
import { FieldHelp } from '../components/guidance/field-help';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { defaultsText } from '../components/library/collection-panel';
import { DownloadOriginal } from '../components/library/download-original';
import { SourcePanel } from '../components/library/source-panel';
import { CitationSources } from '../components/transcript/citations';
import { CrossCheckPanel } from '../components/transcript/cross-check';
import { usePlayback } from '../components/transcript/playback';
import { TranscriptPlayer } from '../components/transcript/player';
import { SpeakerMapping } from '../components/transcript/speaker-mapping';
import { TranscriptView } from '../components/transcript/transcript-view';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { ApiError, api } from '../lib/api';
import {
  collectionTree,
  type DocumentDetail,
  formatDate,
  type LibraryPage,
  useCollections,
  useLibraryDoc,
  useLibraryWrite,
} from '../lib/library';
import { useSchema } from '../lib/schema';
import { useCan } from '../lib/session';
import { useTranscript } from '../lib/transcripts';

// One Library document (PLAN §5.0, §5.0b; task 9b): what it is, how deep it was extracted (raise it later without
// uploading again), its collections, its links to other documents, and the entities its passages mention.
// The original file is never shown inline (PLAN §11): only its Library text; "Download original" saves it as a file.

export const Route = createFileRoute('/library/$id')({
  component: DocumentPage,
  // `?t=` (seconds): a `[T#, mm:ss]` citation opens the transcript at that time.
  validateSearch: (s: Record<string, unknown>): { t?: number } => {
    const t = Number(s.t);
    return Number.isFinite(t) && t >= 0 ? { t: Math.floor(t) } : {};
  },
});

const TOUR = {
  id: 'library-document',
  steps: [
    {
      target: '[data-tour="doc-depth"]',
      title: 'Raise the depth later',
      body: 'A transcript kept at Mentions only can get full Claims extraction once it matters, without uploading it again.',
      learnMore: 'library-and-collections',
    },
    {
      target: '[data-tour="doc-speakers"]',
      title: 'Who is speaking?',
      body: 'A transcript names its speakers "Intervenant A", "Speaker B". Say who each one is: an actor, an anonymous source or the interviewer.',
      learnMore: 'transcripts-and-speakers',
    },
    {
      target: '[data-tour="doc-links"]',
      title: 'Links between documents',
      body: 'A synthesis summarizes its transcripts; a profile sheet profiles a row of an actor list; a newer version replaces an older one.',
    },
  ],
};

function DocumentPage() {
  useScreenTour(TOUR);
  const { id } = Route.useParams();
  const doc = useLibraryDoc(id);
  const { data: snap } = useSchema();
  if (doc.isError)
    return (
      <EmptyState
        icon={FileSearch}
        title={
          doc.error instanceof ApiError && doc.error.status === 404
            ? 'This document is not in the Library'
            : 'The document could not be loaded'
        }
        learnMore="library-and-collections"
      >
        <Link to="/library" className="underline">
          Back to the Library
        </Link>
      </EmptyState>
    );
  if (!doc.data)
    return (
      <div className="space-y-3" aria-busy="true">
        <span className="sr-only">Loading…</span>
        <div className="h-24 animate-pulse rounded-lg bg-muted" />
        <div className="h-64 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  const d = doc.data;
  return (
    <div className="space-y-4">
      <Link to="/library" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" aria-hidden="true" /> Library
      </Link>
      <PageHeader
        title={d.title ?? d.filename}
        description={[DETECTED_KIND_LABELS[d.kind], EXTRACTION_DEPTH_LABELS[d.depth], PIPELINE_STATUS_LABELS[d.status]].join(' · ')}
      />
      <Tip id="library-document" learnMore="library-and-collections">
        Every file is searchable and answerable by chat, whatever its extraction depth. Raise the depth when a file becomes important.
      </Tip>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-4">
          <About doc={d} />
          {d.hasTranscript && <TranscriptCards doc={d} />}
          {(d.hasTranscript || d.kind === 'transcript' || d.kind === 'interview_synthesis') && <CrossCheckPanel documentId={d.id} />}
          <Mentions doc={d} />
          <Passages doc={d} />
        </div>
        <div className="space-y-4">
          {d.kind === 'interview_synthesis' && (
            <Card id="doc-citations" title="Cited transcripts">
              <CitationSources documentId={d.id} />
            </Card>
          )}
          <Collections doc={d} />
          <SourcePanel documentId={d.id} />
          {snap && <InCases schema={snap} target={{ document: d.id }} />}
          <Links doc={d} />
          <EraseSection kind="document" id={d.id} name={d.title ?? d.filename} />
        </div>
      </div>
    </div>
  );
}

/** Transcript and speaker mapping (task 26): the recording's player, the turns with their times, who each speaker is. */
function TranscriptCards({ doc: d }: { doc: DocumentDetail }) {
  const { t } = Route.useSearch();
  const transcript = useTranscript(d.id);
  const canPlay = !!transcript.data?.audio.length;
  const play = usePlayback(t !== undefined ? t * 1000 : null, canPlay);
  if (transcript.isError)
    return (
      <Card id="doc-transcript" title="Transcript">
        <p className="text-sm text-muted-foreground">The transcript could not be loaded.</p>
      </Card>
    );
  if (!transcript.data)
    return (
      <Card id="doc-transcript" title="Transcript">
        <p className="text-sm text-muted-foreground">Loading the transcript…</p>
      </Card>
    );
  const data = transcript.data;
  return (
    <>
      <Card id="doc-speakers" title="Speakers" tour="doc-speakers">
        <SpeakerMapping data={data} onJump={play.pick} />
      </Card>
      <section id="transcript" aria-labelledby="doc-transcript" className="space-y-3 rounded-lg border bg-surface p-4">
        <h2 id="doc-transcript" className="font-semibold">
          Transcript
        </h2>
        {canPlay && <TranscriptPlayer documentId={d.id} audio={data.audio} onTime={play.setCurrentMs} handle={play.handle} />}
        <TranscriptView
          data={data}
          currentMs={play.currentMs}
          focusMs={t !== undefined ? t * 1000 : null}
          onPick={play.pick}
          canPlay={canPlay}
        />
      </section>
    </>
  );
}

function Card({ id, title, children, tour }: { id: string; title: ReactNode; children: ReactNode; tour?: string }) {
  return (
    <section aria-labelledby={id} data-tour={tour} className="space-y-3 rounded-lg border bg-surface p-4">
      <h2 id={id} className="font-semibold">
        {title}
      </h2>
      {children}
    </section>
  );
}

function About({ doc: d }: { doc: DocumentDetail }) {
  const canWrite = useCan('content.write');
  const defaults = defaultsText(d.defaults);
  const facts: [string, ReactNode][] = [
    ['File', <DataText key="f">{d.filename}</DataText>],
    ['Kind', DETECTED_KIND_LABELS[d.kind]],
    ['Status', PIPELINE_STATUS_LABELS[d.status]],
    [
      'Sensitivity',
      d.sensitivity === 'confidential' ? (
        <span key="s" className="inline-flex items-center gap-1">
          <Lock className="size-3.5" aria-hidden="true" /> Confidential: handled by on-platform models only
        </span>
      ) : (
        'Standard'
      ),
    ],
    ['Languages', d.langs.length ? d.langs.join(', ') : '—'],
    ['Uploaded', `${formatDate(d.uploadedAt)}${d.uploadedBy ? ` by ${d.uploadedBy}` : ''}`],
  ];
  if (d.pageCount) facts.push(['Pages', <DataNumber key="p">{d.pageCount}</DataNumber>]);
  if (d.durationS) facts.push(['Duration', `${Math.round(d.durationS / 60)} min`]);
  if (d.sourceDate) facts.push(['Recorded', <DataNumber key="r">{d.sourceDate}</DataNumber>]);
  if (defaults.length) facts.push(['Collection defaults', defaults.join(' · ')]);
  facts.push(['Came in through', arrivalsText(d)]);
  return (
    <Card id="doc-about" title="About this document">
      <dl className="grid gap-x-4 gap-y-1.5 text-sm sm:grid-cols-[10rem_1fr]">
        {facts.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="min-w-0 break-words">{v}</dd>
          </div>
        ))}
      </dl>
      <DownloadOriginal documentId={d.id} />
      {d.sheet && (
        <p className="text-sm">
          Read as a profile sheet
          {d.sheet.code ? (
            <>
              {' '}
              (<DataText>{d.sheet.code}</DataText>)
            </>
          ) : null}
          :{' '}
          <Link to="/sheets/$id" params={{ id: d.id }} className="underline">
            see the sheet import
          </Link>
        </p>
      )}
      <SameText doc={d} />
      <div data-tour="doc-depth" className="space-y-2 border-t pt-3">
        <p className="flex items-center gap-1 text-sm">
          <span className="text-muted-foreground">Extraction depth:</span>
          <span className="font-medium">{EXTRACTION_DEPTH_LABELS[d.depth]}</span>
          <FieldHelp label="Extraction depth" help={EXTRACTION_DEPTH_HELP[d.depth]} learnMore="library-and-collections" />
        </p>
        {canWrite && <RaiseDepth doc={d} />}
      </div>
    </Card>
  );
}

/** "Ingest API · 3 times (also Upload page 1)": the first stream, then every arrival of the same content (task 42). */
function arrivalsText(d: DocumentDetail): string {
  const arrivals = d.arrivals ?? [];
  const total = arrivals.reduce((n, a) => n + a.count, 0);
  const first = SOURCE_CHANNEL_LABELS[d.sourceChannel] ?? SOURCE_CHANNEL_LABELS.manual;
  if (total <= 1) return first;
  const others = arrivals.filter(a => a.channel !== d.sourceChannel);
  return `${first} · arrived ${total} times${others.length ? ` (also ${others.map(a => `${SOURCE_CHANNEL_LABELS[a.channel]} ${a.count}`).join(', ')})` : ''}`;
}

/** Cross-stream duplicates (task 42): the earlier document with the same text, or the later copies of this one. */
function SameText({ doc: d }: { doc: DocumentDetail }) {
  const { of, copies } = d.sameText ?? { of: null, copies: [] };
  if (!of && !copies.length) return null;
  const docLink = (x: { id: string; filename: string; sensitivity: string }) => (
    <Link to="/library/$id" params={{ id: x.id }} className="inline-flex items-center gap-1 underline">
      {x.sensitivity === 'confidential' && <Lock className="size-3.5" aria-label="Confidential" />}
      <DataText>{x.filename}</DataText>
    </Link>
  );
  return (
    <div className="space-y-1.5 border-t pt-3 text-sm" data-testid="same-text">
      {of && (
        <p>
          <span className="font-medium">Same text as</span> {docLink(of)}
          {of.similarity !== null && (
            <>
              {' '}
              (<DataNumber>{of.similarity}</DataNumber>% of this text is in it)
            </>
          )}
          .{' '}
          {d.depth === 'library' && d.sourceChannel !== 'manual'
            ? 'It came in automatically, so it is kept in the Library only and its content is not extracted a second time. Raise the depth to process it anyway.'
            : 'Both are kept.'}
        </p>
      )}
      {copies.length > 0 && (
        <p>
          <span className="font-medium">Also arrived as</span>{' '}
          {copies.map((x, i) => (
            <span key={x.id}>
              {i > 0 && ', '}
              {docLink(x)} <span className="text-muted-foreground">({SOURCE_CHANNEL_LABELS[x.channel]})</span>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

function RaiseDepth({ doc: d }: { doc: DocumentDetail }) {
  const write = useLibraryWrite();
  const confirm = useConfirm();
  const id = useId();
  const [depth, setDepth] = useState<ExtractionDepth | ''>(d.raise.depths[0] ?? '');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const raise = async () => {
    if (!depth) return;
    const ok = await confirm({
      title: `Raise to ${EXTRACTION_DEPTH_LABELS[depth]}?`,
      body: `${EXTRACTION_DEPTH_HELP[depth]} The file is read again (this uses AI); new findings go to review. What was already committed stays.`,
      confirmLabel: 'Raise depth',
    });
    if (!ok) return;
    try {
      await write.mutateAsync({ path: `/library/documents/${d.id}/depth`, body: { depth } });
      setMessage({ ok: true, text: `Queued for ${EXTRACTION_DEPTH_LABELS[depth]} extraction. Follow it under Activity.` });
    } catch (e) {
      setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'The depth could not be raised.' });
    }
  };
  const feedback = message && (
    <p role={message.ok ? 'status' : 'alert'} className={message.ok ? 'w-full text-sm' : 'w-full text-sm text-danger-foreground'}>
      {message.text}
    </p>
  );
  // After raising, the document is queued again: the confirmation stays, the control goes.
  if (!d.raise.depths.length)
    return (
      <>
        {feedback}
        {d.raise.blocker && !message?.ok && <p className="text-sm text-muted-foreground">{d.raise.blocker}</p>}
      </>
    );
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="space-y-1">
        <label htmlFor={id} className="text-xs font-medium">
          Raise depth to
        </label>
        <select
          id={id}
          value={depth}
          onChange={e => setDepth(e.target.value as ExtractionDepth)}
          className="h-9 rounded-md border border-input bg-surface px-2 text-sm"
        >
          {d.raise.depths.map(x => (
            <option key={x} value={x}>
              {EXTRACTION_DEPTH_LABELS[x]}
            </option>
          ))}
        </select>
      </div>
      <Button size="sm" onClick={() => void raise()} disabled={write.isPending}>
        Raise depth
      </Button>
      {feedback}
    </div>
  );
}

function Mentions({ doc: d }: { doc: DocumentDetail }) {
  const { data: snap } = useSchema();
  const { entities, awaitingReview } = d.mentions;
  return (
    <Card id="doc-mentions" title="Entities mentioned">
      {entities.length === 0 && !awaitingReview ? (
        <p className="text-sm text-muted-foreground">
          {d.depth === 'library'
            ? 'Library only: no mentions are looked for. Raise the depth to Mentions only to link its passages to entities.'
            : 'No passage of this document names a known entity yet.'}
        </p>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {snap &&
            entities.map(e => (
              <li key={e.entity.id} className="flex flex-wrap items-center gap-x-2">
                <EntityLink schema={snap} entity={e.entity} />
                <span className="text-muted-foreground">
                  in <DataNumber>{e.passages}</DataNumber> {e.passages === 1 ? 'passage' : 'passages'}
                  {e.confirmed ? '' : ' · suggested'}
                </span>
                <Link to="/library" search={{ entity: e.entity.id }} className="text-xs underline">
                  Other documents
                </Link>
              </li>
            ))}
          {awaitingReview > 0 && (
            <li className="text-muted-foreground">
              {awaitingReview} new {awaitingReview === 1 ? 'name waits' : 'names wait'} for review before{' '}
              {awaitingReview === 1 ? 'it is' : 'they are'} linked.
            </li>
          )}
        </ul>
      )}
    </Card>
  );
}

/** A cold item (Library retention): why its words aren't searchable, and bringing it back. */
function ColdNotice({ doc: d }: { doc: DocumentDetail }) {
  const canWrite = useCan('content.write');
  const write = useLibraryWrite();
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  if (!d.cold) return null;
  const bringBack = async () => {
    setMessage(null);
    try {
      await write.mutateAsync({ path: `/library/documents/${d.id}/warm` });
      setMessage({ ok: true, text: 'Brought back: search finds its words again.' });
    } catch (e) {
      setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'It could not be brought back.' });
    }
  };
  return (
    <div className="space-y-2 rounded-md border bg-muted px-3 py-2 text-sm" data-testid="cold-notice">
      <p>
        <span className="font-medium">In storage since {new Date(d.cold.at).toLocaleDateString()}.</span> It came in automatically and
        nothing cites it, so after a while its passages moved out of the search index. Search still finds it by its name and by meaning; its
        words are found again once it is brought back.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {canWrite && (
          <Button size="sm" variant="outline" onClick={() => void bringBack()} disabled={write.isPending}>
            {write.isPending ? 'Bringing back…' : 'Bring back'}
          </Button>
        )}
        <LearnMore slug="library-retention" />
      </div>
      {message && (
        <p role={message.ok ? 'status' : 'alert'} className={message.ok ? '' : 'text-danger-foreground'}>
          {message.text}
        </p>
      )}
    </div>
  );
}

function Passages({ doc: d }: { doc: DocumentDetail }) {
  return (
    <Card
      id="doc-passages"
      title={
        <>
          Library text{' '}
          <span className="font-normal text-muted-foreground">
            · {d.passages.count} {d.passages.count === 1 ? 'passage' : 'passages'}
          </span>
        </>
      }
    >
      <ColdNotice doc={d} />
      {d.passages.preview.length === 0 ? (
        <p className="text-sm text-muted-foreground">No text yet. It appears once the file has been read.</p>
      ) : (
        <ol className="space-y-2">
          {d.passages.preview.map(p => (
            <li key={p.id} className="rounded-md bg-muted px-3 py-2">
              <DataBlock lang={p.lang ?? undefined} className="line-clamp-6 text-sm whitespace-pre-line">
                {p.text}
              </DataBlock>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function Collections({ doc: d }: { doc: DocumentDetail }) {
  const canWrite = useCan('content.write');
  const write = useLibraryWrite();
  const collections = useCollections();
  const id = useId();
  const [pick, setPick] = useState('');
  const options = collectionTree(collections.data?.collections ?? []).filter(({ c }) => !d.collections.some(x => x.id === c.id));
  return (
    <Card id="doc-collections" title="Collections">
      {d.collections.length === 0 ? (
        <p className="text-sm text-muted-foreground">In no collection. Uploaded as a loose file.</p>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {d.collections.map(c => (
            <li key={c.id} className="flex items-start justify-between gap-2">
              <span className="min-w-0">
                <Link to="/library" search={{ collection: c.id }} className="font-medium hover:underline">
                  <DataText>{c.name}</DataText>
                </Link>
                {c.isGuide && (
                  <span className="ms-1 inline-flex items-center gap-1 rounded bg-muted px-1.5 text-xs">
                    <BookMarked className="size-3" aria-hidden="true" /> Reading guide
                  </span>
                )}
                {c.path && <DataBlock className="text-xs break-all text-muted-foreground">{c.path}</DataBlock>}
              </span>
              {canWrite && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-7"
                  aria-label={`Remove from ${c.name}`}
                  onClick={() => write.mutate({ path: `/library/documents/${d.id}/collections/${c.id}`, method: 'DELETE' })}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canWrite && options.length > 0 && (
        <form
          className="flex items-end gap-2 border-t pt-3"
          onSubmit={e => {
            e.preventDefault();
            if (pick)
              write.mutate(
                { path: `/library/documents/${d.id}/collections`, body: { collectionId: pick } },
                { onSuccess: () => setPick('') },
              );
          }}
        >
          <div className="min-w-0 flex-1 space-y-1">
            <label htmlFor={id} className="text-xs font-medium">
              Add to collection
            </label>
            <select
              id={id}
              value={pick}
              onChange={e => setPick(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-surface px-2 text-sm"
            >
              <option value="">Choose…</option>
              {options.map(({ c, depth }) => (
                <option key={c.id} value={c.id}>
                  {`${'  '.repeat(depth)}${c.name}`}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" size="sm" variant="outline" disabled={!pick || write.isPending}>
            Add
          </Button>
        </form>
      )}
    </Card>
  );
}

const linkLabel = (type: DocumentLinkType, direction: 'from' | 'to') => DOCUMENT_LINK_LABELS[type][direction];

function Links({ doc: d }: { doc: DocumentDetail }) {
  const canWrite = useCan('content.write');
  const write = useLibraryWrite();
  const typeId = useId();
  const docId = useId();
  const [type, setType] = useState<DocumentLinkType>('related');
  const [target, setTarget] = useState<{ id: string; filename: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchDocs = async (q: string) =>
    (await api<LibraryPage>(`/library/documents?${new URLSearchParams({ q, limit: '8' })}`)).documents.filter(x => x.id !== d.id);
  const add = async () => {
    if (!target) return;
    setError(null);
    try {
      await write.mutateAsync({ path: `/library/documents/${d.id}/links`, body: { toDocumentId: target.id, type } });
      setTarget(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The link could not be added.');
    }
  };
  return (
    <Card id="doc-links" title="Linked documents" tour="doc-links">
      {d.links.length === 0 ? (
        <p className="text-sm text-muted-foreground">No links yet.</p>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {d.links.map(l => (
            <li key={`${l.id ?? 'auto'}-${l.direction}-${l.otherId}`} className="flex items-start justify-between gap-2">
              <span className="min-w-0">
                <span className="text-muted-foreground">{linkLabel(l.type, l.direction)} </span>
                <Link to="/library/$id" params={{ id: l.otherId }} className="font-medium break-all hover:underline">
                  <DataText>{l.other.filename}</DataText>
                </Link>
                {l.automatic && <span className="text-xs text-muted-foreground"> (sheet versions)</span>}
                {l.note && <DataBlock className="text-xs text-muted-foreground">{l.note}</DataBlock>}
              </span>
              {canWrite && l.id && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-7"
                  aria-label={`Remove the link to ${l.other.filename}`}
                  onClick={() => write.mutate({ path: `/library/links/${l.id}`, method: 'DELETE' })}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canWrite && (
        <form
          aria-label="Link to another document"
          className="space-y-2 border-t pt-3"
          onSubmit={e => {
            e.preventDefault();
            void add();
          }}
        >
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <label htmlFor={typeId} className="text-xs font-medium">
                Link type
              </label>
              <FieldHelp label="Link type" help={DOCUMENT_LINK_LABELS[type].help} learnMore="library-and-collections" />
            </div>
            <select
              id={typeId}
              value={type}
              onChange={e => setType(e.target.value as DocumentLinkType)}
              className="h-9 w-full rounded-md border border-input bg-surface px-2 text-sm"
            >
              {DOCUMENT_LINK_TYPES.map(t => (
                <option key={t} value={t}>
                  {DOCUMENT_LINK_LABELS[t].from}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor={docId} className="text-xs font-medium">
              Document
            </label>
            <Combobox<{ id: string; filename: string }>
              id={docId}
              placeholder="Type part of a file name"
              search={searchDocs}
              getKey={x => x.id}
              getLabel={x => x.filename}
              onSelect={x => setTarget({ id: x.id, filename: x.filename })}
              emptyText="No document with this name"
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-danger-foreground">
              {error}
            </p>
          )}
          <Button type="submit" size="sm" variant="outline" disabled={!target || write.isPending}>
            Add link
          </Button>
        </form>
      )}
    </Card>
  );
}
